import { Button, Dialog } from '@/components/UI'
import { useTranslation } from 'react-i18next'

type Props = {
  opened: boolean
  onClose: () => void
  onConfirm: () => void
  /** 消費するスタンプ数（STAMP_COUNT condition の threshold） */
  consumptionCount: number
  /** 賞品名（LOTTERY 以外で表示） */
  rewardName?: string | null
  /**
   * 賞品選択方式。
   * LOTTERY → 「抽選する」、USER_SELECT → 「応募する」、その他 → 「獲得する」
   */
  prizeSelectionType?: 'SINGLE' | 'GIVE_ALL' | 'USER_SELECT' | 'LOTTERY' | null
}

/**
 * スタンプ消費確認ダイアログ。
 * isStampConsumptionRequired=true の特典で、獲得前にユーザーに消費を確認させる。
 * Figma: node 553-19111
 */
export function DialogV2StampConsumptionConfirm({
  opened,
  onClose,
  onConfirm,
  consumptionCount,
  rewardName,
  prizeSelectionType,
}: Props) {
  const { t } = useTranslation()
  const { message, buttonLabel } = resolveDialogContent(t, prizeSelectionType, rewardName ?? null, consumptionCount)

  return (
    <Dialog selectorState={opened} closeFunc={onClose} title={t('reward_detail.stamp_consumption_confirm.title')}>
      <div className="flex flex-col gap-4 px-4 py-6">
        <p className="text-center text-[13px] leading-[20px] text-gray-600">{message}</p>
        <Button label={buttonLabel} variant="primary" size="md" fullWidth onClick={onConfirm} />
        <button
          type="button"
          className="flex h-[48px] w-full items-center justify-center rounded-[8px] border border-gray-300 text-[15px] font-bold text-gray-600"
          onClick={onClose}
        >
          {t('reward_detail.stamp_consumption_confirm.cancel')}
        </button>
      </div>
    </Dialog>
  )
}

type TFunction = ReturnType<typeof useTranslation>['t']

export function resolveDialogContent(
  t: TFunction,
  prizeSelectionType: Props['prizeSelectionType'],
  rewardName: string | null,
  consumptionCount: number,
): { message: string; buttonLabel: string } {
  if (prizeSelectionType === 'LOTTERY') {
    return {
      message: t('reward_detail.stamp_consumption_confirm.lottery_message', { count: consumptionCount }),
      buttonLabel: t('reward_detail.stamp_consumption_confirm.lottery_button'),
    }
  }
  if (rewardName != null) {
    // USER_SELECT は応募フォームを伴うことが多い
    const isUserSelect = prizeSelectionType === 'USER_SELECT'
    return {
      message: t(
        isUserSelect
          ? 'reward_detail.stamp_consumption_confirm.user_select_message'
          : 'reward_detail.stamp_consumption_confirm.default_message',
        { count: consumptionCount, rewardName },
      ),
      buttonLabel: t(
        isUserSelect
          ? 'reward_detail.stamp_consumption_confirm.user_select_button'
          : 'reward_detail.stamp_consumption_confirm.default_button',
      ),
    }
  }
  return {
    message: t('reward_detail.stamp_consumption_confirm.default_message_no_name', { count: consumptionCount }),
    buttonLabel: t('reward_detail.stamp_consumption_confirm.default_button'),
  }
}
